import { Lock, FileSearch, ShieldCheck, Building2, Globe } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const BADGES = [
  {
    icon: ShieldCheck,
    title: "GDPR compliant",
    text: "Fully compliant with GDPR and CCPA. Your personal data is never touched or sold.",
    testid: "security-badge-gdpr",
  },
  {
    icon: Lock,
    title: "Encrypted connections",
    text: "All shared traffic travels through fully encrypted channels end to end.",
    testid: "security-badge-encryption",
  },
  {
    icon: FileSearch,
    title: "Zero file access",
    text: "Honeygain never accesses your files, photos, browsing history or personal documents.",
    testid: "security-badge-privacy",
  },
  {
    icon: Building2,
    title: "Vetted partners only",
    text: "Bandwidth is used exclusively by verified businesses for legitimate web intelligence.",
    testid: "security-badge-partners",
  },
  {
    icon: Globe,
    title: "ISP & home-IP safety",
    text: "Only bandwidth is shared — never your files, browsing or identity. Traffic is encrypted end to end. Read Honeygain's acceptable-use terms.",
    link: "https://www.honeygain.com/terms-of-use/",
    linkLabel: "Read the terms",
    testid: "security-badge-isp",
  },
];

const SecurityTrust = () => (
  <section id="security" data-testid="security-trust-section" className="py-20 sm:py-28 bg-zinc-900 text-white relative overflow-hidden">
    <div
      aria-hidden
      className="pointer-events-none absolute -bottom-40 -left-40 w-[36rem] h-[36rem] rounded-full bg-[radial-gradient(circle,rgba(255,184,0,0.16),transparent_65%)]"
    />
    <div className="mx-auto max-w-7xl px-5 sm:px-8 relative">
      <Reveal>
        <p className="text-xs font-bold uppercase tracking-[0.25em] text-amber-400">Safety first</p>
        <h2 className="mt-3 font-display text-3xl sm:text-4xl font-extrabold tracking-tight max-w-2xl">
          Your privacy stays yours. Always.
        </h2>
        <p className="mt-4 text-base text-zinc-400 leading-relaxed max-w-2xl">
          The number one question people ask: is it safe? Honeygain is built so the app can only
          share bandwidth — nothing else. Here is exactly how you are protected.
        </p>
      </Reveal>

      <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {BADGES.map(({ icon: Icon, title, text, link, linkLabel, testid }, i) => (
          <Reveal key={title} delay={i * 0.1}>
            <div
              data-testid={testid}
              className="h-full rounded-3xl border border-zinc-700/70 bg-zinc-800/50 backdrop-blur-sm p-7 hover:border-amber-400/50 transition-colors"
            >
              <div className="grid place-items-center w-11 h-11 rounded-2xl bg-amber-400 text-zinc-900">
                <Icon className="w-5 h-5" />
              </div>
              <h3 className="mt-5 font-display text-lg font-bold">{title}</h3>
              <p className="mt-2 text-sm text-zinc-400 leading-relaxed">{text}</p>
              {link && (
                <a
                  href={link}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="security-terms-link"
                  className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-amber-400 hover:text-amber-300 transition-colors"
                >
                  {linkLabel} →
                </a>
              )}
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export default SecurityTrust;
